import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Skeleton, Alert, Tabs, TimeRangeFilter } from '@bbpos/ui';
import type { TimeRangeValue } from '@bbpos/ui';
import { dashboardService } from '@/services/dashboard.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { useDashboardSocket } from '@/hooks/use-dashboard-socket';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import { posUrl } from '@/lib/pos-url';
import { ROLE_LEVELS } from '@/types/user';
import { DashboardKpiRow } from './DashboardKpiRow';
import { SalesWaveSection } from './SalesWaveSection';
import { storeTimezone } from '@/lib/store-clock';
import { resolveSalesWaveParams } from './sales-wave-params';
import { PaymentModeWidget } from './PaymentModeWidget';
import { LowStockWidget } from './LowStockWidget';
import { TopCategoriesWidget } from './TopCategoriesWidget';
import { TopProductsWidget } from './TopProductsWidget';
import { RecentBillsWidget } from './RecentBillsWidget';
import { ActiveBillsWidget } from './ActiveBillsWidget';
import { SalesHeatmapWidget } from './SalesHeatmapWidget';
import { OrderTypeMixWidget } from './OrderTypeMixWidget';
import { CustomerSplitWidget } from './CustomerSplitWidget';
import { StaffInsightBanner } from './StaffInsightBanner';
import { QuickActionsBar } from './QuickActionsBar';
import { WelcomeCard } from './WelcomeCard';

function buildTabs(isRestaurant: boolean) {
  const items = [
    { id: 'overview', label: 'Overview' },
    { id: 'sales', label: 'Sales' },
  ];
  if (isRestaurant) {
    items.push({ id: 'orders', label: 'Orders' });
  }
  return items;
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant="rect" height={120} />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Skeleton variant="rect" height={320} />
        </div>
        <div className="flex flex-col gap-6">
          <Skeleton variant="rect" height={180} />
          <Skeleton variant="rect" height={100} />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Skeleton variant="rect" height={240} />
        <Skeleton variant="rect" height={240} />
      </div>
    </div>
  );
}

export function TenantDashboardContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.profile);
  const locationId = useDashboardStore((s) => s.selectedLocationId);
  const timeRange = useDashboardStore((s) => s.timeRange);
  const setTimeRange = useDashboardStore((s) => s.setTimeRange);
  const period = useDashboardStore((s) => s.period);
  const setPeriod = useDashboardStore((s) => s.setPeriod);
  const insightDismissed = useDashboardStore((s) => s.insightDismissed);
  const dismissInsight = useDashboardStore((s) => s.dismissInsight);
  const activeTab = useDashboardStore((s) => s.activeTab);
  const setActiveTab = useDashboardStore((s) => s.setActiveTab);

  useDashboardSocket(queryClient);

  const refreshAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }, [queryClient]);
  useKeyboardShortcut('r', refreshAll);

  const roleLevel = ROLE_LEVELS[user?.role ?? ''] ?? 0;
  if (roleLevel < ROLE_LEVELS.MANAGER) {
    return (
      <Alert
        variant="danger"
        title="Access Denied"
        description="You do not have permission to access the dashboard. Contact your business owner."
      />
    );
  }

  const isOwner = roleLevel >= ROLE_LEVELS.OWNER;
  // The stores this user's 'All locations' spans (T22): every store for the
  // owner, a manager's assigned ones.
  const activeStoreCount = (profile?.locations ?? []).filter((l) => l.is_active).length;
  const isRestaurant = profile?.tenant?.business_type === 'RESTAURANT';
  const tabs = buildTabs(isRestaurant);

  const waveParams = resolveSalesWaveParams(timeRange as {
    preset?: string;
    startDate?: string;
    endDate?: string;
  }, storeTimezone(profile, locationId));

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useQuery({
    queryKey: ['dashboard', 'summary', locationId],
    queryFn: () => dashboardService.getSummary(locationId),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const { data: salesWave, isLoading: waveLoading } = useQuery({
    queryKey: [
      'dashboard',
      'sales-wave',
      locationId,
      waveParams.date,
      waveParams.compareDate,
      waveParams.days,
    ],
    queryFn: () => dashboardService.getSalesWave({ locationId, ...waveParams }),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  if (summaryLoading) {
    return <DashboardSkeleton />;
  }

  if (summaryError || !summary) {
    return (
      <Alert
        variant="danger"
        title="Failed to load dashboard"
        description="Something went wrong loading dashboard data. Please try refreshing the page."
        action={{ label: 'Retry', onClick: refreshAll }}
      />
    );
  }

  const isNewTenant =
    summary.today.bill_count === 0 &&
    summary.yesterday.bill_count === 0 &&
    summary.active_bills === 0;

  const hasSalesToday = summary.today.bill_count > 0;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Gradient hero header */}
      <div className="bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE4YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2em0wIDMwYzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2ek02IDE4YzMuMzE0IDAgNi0yLjY4NiA2LTZTOS4zMTQgNiA2IDYgMCA4LjY4NiAwIDEyczIuNjg2IDYgNiA2ek02IDQ4YzMuMzE0IDAgNi0yLjY4NiA2LTZzLTIuNjg2LTYtNi02LTYgMi42ODYtNiA2IDIuNjg2IDYgNiA2eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
        <div className="relative">
          <h1 className="text-2xl font-bold tracking-tight text-white">
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {profile?.name?.split(' ')[0] ?? 'there'}
          </h1>
          <p className="text-brand-100 mt-1 text-sm">
            Here&apos;s how {profile?.tenant?.business_name ?? 'your business'} is doing today
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <Tabs
          items={tabs}
          activeId={activeTab}
          onChange={setActiveTab}
          variant="pill"
        />
        <div className="flex flex-wrap items-center gap-3">
          {/* Dashboard-wide period — drives range-aware widgets
              (Top Categories, future range KPIs). KPI cards stay locked
              to today-vs-yesterday until the summary endpoint gains range
              support; the chip is still useful as the single control for
              the rest of the page. */}
          <TimeRangeFilter
            value={period as TimeRangeValue}
            onChange={(v) => setPeriod(v as typeof period)}
            presets={[
              { id: 'today',     label: 'Today' },
              { id: 'yesterday', label: 'Yesterday' },
              { id: 'last7',     label: 'Last 7 Days' },
              { id: 'last30',    label: 'Last 30 Days' },
              { id: 'custom',    label: 'Custom' },
            ]}
          />
          <QuickActionsBar />
        </div>
      </div>

      {isNewTenant && (
        <div className="animate-slide-up">
          <WelcomeCard
            businessName={profile?.tenant?.business_name ?? 'your business'}
            steps={[
              {
                label: 'Add your first location',
                to: '/locations/new',
                done: summary.onboarding?.has_location ?? (profile?.locations?.length ?? 0) > 0,
              },
              {
                label: 'Add products to your catalog',
                to: '/products/new',
                done: summary.onboarding?.has_products ?? false,
              },
              {
                label: 'Invite your team',
                to: '/users/new',
                done: summary.onboarding?.has_team ?? false,
              },
              {
                label: 'Complete your first sale',
                to: '/reports',
                done: summary.onboarding?.first_sale_completed ?? false,
              },
            ]}
          />
        </div>
      )}

      <DashboardKpiRow
        data={summary}
        loading={summaryLoading}
        onApprovalsClick={() => navigate('/approvals')}
        onRevenueClick={() => navigate('/reports/sales')}
        onBillsClick={() => navigate('/reports/sales')}
        onAvgClick={() => navigate('/reports/sales')}
        onProfitClick={() => navigate('/reports/pnl')}
      />

      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {hasSalesToday ? (
                <SalesWaveSection
                  data={salesWave}
                  loading={waveLoading}
                  timeRange={timeRange}
                  onTimeRangeChange={setTimeRange}
                />
              ) : (
                <div className="rounded-xl bg-gradient-to-br from-white via-brand-50/30 to-indigo-50/40 dark:from-neutral-900 dark:via-brand-950/20 dark:to-indigo-950/20 backdrop-blur-sm shadow-md p-8 border border-brand-100 dark:border-brand-900/40">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white shadow-lg shadow-brand-500/25 mb-4">
                      <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-1">No sales yet today</h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">Start billing to see your sales wave chart light up</p>
                    <button
                      onClick={() => window.open(posUrl('/pos/billing'), '_blank')}
                      className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-brand-500 to-indigo-500 text-white font-medium text-sm shadow-md shadow-brand-500/25 hover:shadow-lg hover:shadow-brand-500/30 transition-all duration-200 hover:-translate-y-0.5"
                    >
                      Open POS
                    </button>
                  </div>
                </div>
              )}

              {/* 7-day × 24-hour heatmap — surfaces the busiest weekday/hour
                  combinations so owners can spot patterns the daily Sales
                  Wave can't show. Fetches its own data. */}
              <SalesHeatmapWidget />
            </div>
            <div className="flex flex-col gap-6">
              <PaymentModeWidget
                breakdown={summary.today.payment_breakdown}
                loading={summaryLoading}
              />
              {isRestaurant && (
                <OrderTypeMixWidget breakdown={summary.order_type_breakdown} />
              )}
              <CustomerSplitWidget
                newCount={summary.customer_split?.new_count ?? 0}
                returningCount={summary.customer_split?.returning_count ?? 0}
              />
              <LowStockWidget
                count={summary.low_stock_count}
                acrossStores={!locationId && activeStoreCount > 1}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopProductsWidget />
            <RecentBillsWidget />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopCategoriesWidget />
            <ActiveBillsWidget count={summary.active_bills} />
          </div>

          {isOwner && (
            <StaffInsightBanner
              hourly={salesWave?.today ?? []}
              totalBillsToday={summary.today.bill_count}
              dismissed={insightDismissed}
              onDismiss={dismissInsight}
            />
          )}
        </>
      )}

      {activeTab === 'sales' && (
        <div className="space-y-6 animate-fade-in">
          <SalesWaveSection
            data={salesWave}
            loading={waveLoading}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
          />
          <SalesHeatmapWidget />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopProductsWidget />
            <TopCategoriesWidget />
          </div>
          <PaymentModeWidget
            breakdown={summary.today.payment_breakdown}
            loading={summaryLoading}
          />
        </div>
      )}

      {activeTab === 'orders' && isRestaurant && (
        <div className="space-y-6 animate-fade-in">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ActiveBillsWidget count={summary.active_bills} />
            <RecentBillsWidget />
          </div>
        </div>
      )}
    </div>
  );
}
